import { useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { Layout } from "../components/Layout"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/Button"
import { Skeleton } from "@/components/Skeleton/Skeleton"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog/Dialog"
import { Input } from "@/components/Form/Input"
import { useToast } from "@/components/Toast"
import { StatementStatusBadge } from "@/components/AffiliateStatements"
import {
  getStatementCsv,
  useGenerateStatement,
  useMarkStatementPaid,
  useAffiliateStatement,
  useSendStatement,
  useStatementEmailPreview,
  type MarkStatementPaidPayload,
  type SendStatementResult,
} from "@/hooks/useAffiliateStatements"
import { APIError } from "@/api"
import {
  formatCents,
  formatDateTimeUtc,
  formatDateUtc,
  periodLabel,
} from "@/lib/affiliateStatements"
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline"

function Stat({
  label,
  value,
  tone = "neutral",
}: {
  label: string
  value: string
  tone?: "good" | "bad" | "neutral"
}) {
  const valueColor =
    tone === "good" ? "text-green-400" : tone === "bad" ? "text-amber-400" : "text-primary"
  return (
    <div className="rounded-md border border-border bg-background/40 p-3">
      <div className="text-xs text-secondary">{label}</div>
      <div className={`text-xl font-semibold ${valueColor}`}>{value}</div>
    </div>
  )
}

const errorMessage = (err: unknown) =>
  err instanceof APIError ? err.originalError.message : "Request failed"

export function AffiliateStatement() {
  const { statementId } = useParams<{ statementId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const publicId = statementId || ""

  const { data, isLoading, error } = useAffiliateStatement(publicId)
  const preview = useStatementEmailPreview(publicId)
  const generate = useGenerateStatement()
  const send = useSendStatement()
  const markPaid = useMarkStatementPaid()

  const [showSendConfirm, setShowSendConfirm] = useState(false)
  const [showPaidDialog, setShowPaidDialog] = useState(false)
  const [paidForm, setPaidForm] = useState({
    method: "check" as MarkStatementPaidPayload["method"],
    reference: "",
    adminNotes: "",
  })
  const [sendResult, setSendResult] = useState<SendStatementResult | null>(null)
  const [downloading, setDownloading] = useState(false)

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  if (isLoading || !data) {
    return (
      <Layout>
        <div className="space-y-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    )
  }

  const { statement, affiliate, lines, recipients, generated_by_user, sent_by_user, payment } = data
  const label = periodLabel(statement.period_start, statement.period_end)
  const isDraft = statement.status === "draft"
  const isSent = statement.status === "sent"
  // A draft built while its period was running is missing later payments; it must be rebuilt
  // before it can go out.
  const stalePreview =
    isDraft &&
    statement.period_complete &&
    new Date(statement.generated_at).getTime() < new Date(statement.period_end).getTime()
  const periodEndsOn = formatDateUtc(new Date(new Date(statement.period_end).getTime() - 1))

  const onRegenerate = async () => {
    try {
      await generate.mutateAsync({
        affiliatePublicId: affiliate.public_id,
        periodStart: statement.period_start,
      })
      toast({ title: "Draft regenerated", description: label })
    } catch (err) {
      toast({
        title: "Error regenerating statement",
        description: errorMessage(err),
        variant: "destructive",
      })
    }
  }

  const onSend = async () => {
    try {
      const result = await send.mutateAsync(publicId)
      setSendResult(result)
      toast({
        title: isSent ? "Statement re-sent" : "Statement sent",
        description: `Delivered to ${result.delivered_to.join(", ")}`,
      })
    } catch (err) {
      toast({ title: "Error sending statement", description: errorMessage(err), variant: "destructive" })
    }
  }

  const onMarkPaid = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await markPaid.mutateAsync({
        statementPublicId: publicId,
        method: paidForm.method,
        reference: paidForm.reference.trim() || undefined,
        adminNotes: paidForm.adminNotes.trim() || undefined,
      })
      toast({
        title: "Marked paid",
        description:
          statement.amount_due_cents > 0 ? formatCents(statement.amount_due_cents) : "Settled at $0.00",
      })
      setShowPaidDialog(false)
      setPaidForm({ method: "check", reference: "", adminNotes: "" })
    } catch (err) {
      toast({ title: "Error marking paid", description: errorMessage(err), variant: "destructive" })
    }
  }

  const onDownloadCsv = async () => {
    setDownloading(true)
    try {
      const blob = await getStatementCsv(publicId)
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement("a")
      anchor.href = url
      anchor.download = `${publicId}.csv`
      anchor.click()
      window.URL.revokeObjectURL(url)
    } catch {
      toast({
        title: "Download failed",
        description: "The CSV could not be downloaded. Please try again.",
        variant: "destructive",
      })
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          title={
            <span className="flex items-center gap-2">
              <DocumentTextIcon className="h-6 w-6 text-secondary" />
              {label} statement
              <StatementStatusBadge status={statement.status} />
            </span>
          }
          description={
            <span>
              <Link
                to={`/affiliates/${affiliate.public_id}`}
                className="font-medium text-blue-600 hover:underline"
              >
                {affiliate.name}
              </Link>{" "}
              · {formatDateUtc(statement.period_start)} to {periodEndsOn} (UTC) ·{" "}
              {statement.commission_percent}% commission
            </span>
          }
          actions={
            <Button variant="outline" onClick={() => navigate(`/affiliates/${affiliate.public_id}`)}>
              <ArrowLeftIcon className="h-4 w-4" />
              Back to Affiliate
            </Button>
          }
        />

        <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
          <h2 className="text-lg font-semibold text-primary">Summary</h2>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Gross" value={formatCents(statement.gross_cents)} />
            <Stat label="Tax" value={formatCents(-statement.tax_cents)} />
            <Stat label="Processing fees" value={formatCents(-statement.fee_cents)} />
            <Stat label="Refunds" value={formatCents(-statement.refund_cents)} />
            <Stat label="Net revenue" value={formatCents(statement.net_revenue_cents)} />
            <Stat label={`Commission (${statement.commission_percent}%)`} value={formatCents(statement.commission_cents)} />
            <Stat label="Carried balance" value={formatCents(statement.carried_balance_cents)} />
            <Stat
              label="Amount due"
              value={formatCents(statement.amount_due_cents)}
              tone={statement.amount_due_cents > 0 && statement.status !== "paid" ? "bad" : "good"}
            />
          </div>
          <div className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm text-secondary sm:grid-cols-2">
            <div>Payment due by: {formatDateUtc(statement.payment_due_at)}</div>
            <div>
              Generated: {formatDateTimeUtc(statement.generated_at)} by{" "}
              {generated_by_user ? generated_by_user.name || generated_by_user.public_id : "the daily job"}
            </div>
            <div>
              Sent:{" "}
              {statement.sent_at
                ? `${formatDateTimeUtc(statement.sent_at)} by ${
                    sent_by_user ? sent_by_user.name || sent_by_user.public_id : "the daily job"
                  }`
                : "not sent"}
            </div>
            {!statement.period_complete && (
              <div className="text-amber-400">
                The period is still running, so this is a preview and can't be sent yet.
              </div>
            )}
          </div>
          {statement.status === "paid" && (
            <div className="rounded-md border border-green-700/40 bg-green-900/20 p-3 text-sm text-green-300">
              {payment ? (
                <>
                  Paid {formatCents(payment.amount_cents)} on {formatDateUtc(payment.paid_at)} by{" "}
                  {payment.method}
                  {payment.reference ? `, reference ${payment.reference}` : ""}
                  {payment.admin_notes ? `. Notes: ${payment.admin_notes}` : ""}
                </>
              ) : (
                "Settled with nothing due, so no payment was recorded."
              )}
            </div>
          )}
        </section>

        <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
          <h2 className="text-lg font-semibold text-primary">Actions</h2>
          <div className="flex flex-wrap gap-2">
            {isDraft && (
              <Button variant="outline" onClick={() => void onRegenerate()} disabled={generate.isPending}>
                <ArrowPathIcon className="h-4 w-4" />
                {generate.isPending ? "Regenerating..." : "Regenerate"}
              </Button>
            )}
            {statement.status !== "paid" && (
              <Button
                onClick={() => setShowSendConfirm(true)}
                disabled={send.isPending || !statement.period_complete || stalePreview}
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                {send.isPending ? "Sending..." : isSent ? "Resend" : "Send"}
              </Button>
            )}
            {isSent && (
              <Button variant="outline" onClick={() => setShowPaidDialog(true)}>
                <CurrencyDollarIcon className="h-4 w-4" />
                Mark paid
              </Button>
            )}
            <Button variant="outline" onClick={() => void onDownloadCsv()} disabled={downloading}>
              <ArrowDownTrayIcon className="h-4 w-4" />
              {downloading ? "Downloading..." : "Download CSV"}
            </Button>
          </div>
          {stalePreview && (
            <p className="text-sm text-amber-400">
              This statement was generated before {label} ended. Regenerate it, then send.
            </p>
          )}
          <p className="text-xs text-secondary">
            {isDraft
              ? "Regenerating recomputes the numbers from Stripe. Once sent, the numbers are frozen."
              : isSent
                ? "Sent statements are frozen. Resending delivers the same numbers again."
                : "Paid statements are frozen."}
          </p>

          {statement.warnings.length > 0 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-900/20 p-3 text-sm text-amber-300 space-y-1">
              <div className="font-medium">
                {statement.warnings.length === 1
                  ? "1 warning"
                  : `${statement.warnings.length} warnings`}{" "}
                from the last generation. Check them before sending.
              </div>
              <ul className="list-disc pl-5 space-y-0.5">
                {statement.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {statement.send_failures.length > 0 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-900/20 p-3 text-sm text-amber-300 space-y-1">
              <div className="font-medium">
                Not delivered to {statement.send_failures.map((f) => f.email).join(", ")}
              </div>
              <ul className="list-disc pl-5 space-y-0.5">
                {statement.send_failures.map((f) => (
                  <li key={f.email}>
                    {f.email}: {f.error}
                  </li>
                ))}
              </ul>
              <div>Use Resend to try again; everyone on the list gets the email again.</div>
            </div>
          )}

          {sendResult && (
            <div className="rounded-md border border-border bg-background/40 p-3 text-sm space-y-1">
              <div className="font-medium text-primary">Last send</div>
              <div className="text-green-400">Delivered to: {sendResult.delivered_to.join(", ")}</div>
              {sendResult.failed.length > 0 && (
                <ul className="text-amber-400 list-disc pl-5">
                  {sendResult.failed.map((f) => (
                    <li key={f.email}>
                      Failed: {f.email} ({f.error})
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>

        <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
          <h2 className="text-lg font-semibold text-primary">Line items ({lines.length})</h2>
          {lines.length === 0 ? (
            <p className="text-sm text-secondary">No qualifying payments or refunds in this period.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-secondary border-b border-border">
                    <th className="py-2 pr-2">Date</th>
                    <th className="py-2 pr-2">Type</th>
                    <th className="py-2 pr-2">Team</th>
                    <th className="py-2 pr-2">Invoice</th>
                    <th className="py-2 pr-2">Commission ends</th>
                    <th className="py-2 pr-2 text-right">Gross</th>
                    <th className="py-2 pr-2 text-right">Tax</th>
                    <th className="py-2 pr-2 text-right">Fees</th>
                    <th className="py-2 pr-2 text-right">Net</th>
                    <th className="py-2 pr-2 text-right">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <tr
                      key={`${line.stripe_invoice_id}:${line.stripe_refund_id ?? "payment"}`}
                      className="border-b border-border/50"
                    >
                      <td className="py-2 pr-2 whitespace-nowrap">{formatDateUtc(line.occurred_at)}</td>
                      <td className="py-2 pr-2">
                        {line.kind}
                        {new Date(line.occurred_at) < new Date(statement.period_start) && (
                          <span className="ml-1 text-xs text-amber-400">(earlier)</span>
                        )}
                      </td>
                      <td className="py-2 pr-2">{line.team_name}</td>
                      <td className="py-2 pr-2 font-mono text-xs">{line.stripe_invoice_id}</td>
                      <td className="py-2 pr-2 whitespace-nowrap text-secondary">
                        {formatDateUtc(line.first_year_ends_at)}
                      </td>
                      <td className="py-2 pr-2 text-right">{formatCents(line.gross_cents)}</td>
                      <td className="py-2 pr-2 text-right">{formatCents(line.tax_cents)}</td>
                      <td className="py-2 pr-2 text-right">{formatCents(line.fee_cents)}</td>
                      <td className="py-2 pr-2 text-right">{formatCents(line.net_cents)}</td>
                      <td className="py-2 pr-2 text-right font-medium">
                        {formatCents(line.commission_cents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="rounded-lg border border-border bg-surface p-5 space-y-3">
          <h2 className="text-lg font-semibold text-primary">Email preview</h2>
          <div className="text-sm text-secondary">
            Recipients: {recipients.join(", ")}
          </div>
          {preview.error ? (
            <p className="text-sm text-amber-400">Couldn't render the email preview.</p>
          ) : !preview.data ? (
            <Skeleton className="h-96 w-full" />
          ) : (
            <>
              <div className="text-sm">
                <span className="text-secondary">Subject: </span>
                <span className="text-primary">{preview.data.subject}</span>
              </div>
              {/* Sandboxed with no permissions: the HTML is rendered, never run. */}
              <iframe
                title="Statement email preview"
                sandbox=""
                srcDoc={preview.data.html}
                className="h-[640px] w-full rounded-md border border-border bg-white"
              />
            </>
          )}
        </section>
      </div>

      <ConfirmDialog
        open={showSendConfirm}
        onOpenChange={setShowSendConfirm}
        title={isSent ? "Resend this statement?" : "Send this statement?"}
        description={`Emails the ${label} statement for ${affiliate.name} to ${recipients.join(", ")}.${
          isDraft ? " Sending marks it sent and freezes the numbers." : " The numbers are unchanged."
        }`}
        confirmLabel={isSent ? "Resend" : "Send"}
        onConfirm={() => void onSend()}
      />

      <Dialog open={showPaidDialog} onOpenChange={setShowPaidDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Mark {statement.amount_due_cents > 0 ? formatCents(statement.amount_due_cents) : "$0.00"}{" "}
              paid
            </DialogTitle>
            <DialogDescription>
              Records that the payment for {label} has actually gone out. This does not send money,
              cut the check or transfer first, then record it here.
              {statement.amount_due_cents === 0 &&
                " Nothing is due, so this settles the statement without a payment record."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onMarkPaid} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Method</label>
              <select
                value={paidForm.method}
                onChange={(e) =>
                  setPaidForm((s) => ({ ...s, method: e.target.value as typeof s.method }))
                }
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              >
                <option value="check">Check</option>
                <option value="ach">ACH</option>
                <option value="paypal">PayPal</option>
                <option value="wire">Wire</option>
                <option value="other">Other</option>
              </select>
            </div>
            <Input
              name="reference"
              label="Reference (check #, transaction id, optional)"
              value={paidForm.reference}
              onChange={(e) => setPaidForm((s) => ({ ...s, reference: e.target.value }))}
            />
            <Input
              name="notes"
              label="Notes (optional)"
              value={paidForm.adminNotes}
              onChange={(e) => setPaidForm((s) => ({ ...s, adminNotes: e.target.value }))}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setShowPaidDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={markPaid.isPending}>
                {markPaid.isPending ? "Recording..." : "Confirm paid"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  )
}
